/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
	Box,
	Toolbar,
	Typography,
	IconButton,
	Grid,
	Stack,
	useTheme,
	useMediaQuery
} from '@mui/material';

// Icons
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { TbWindowMinimize } from 'react-icons/tb';
import { callTimerFormat } from '../../../../../../../libs/HelperContent';
import { useSelector } from 'react-redux';
import { CallContext } from '../../../../../../../libs/Context';
import ShareScreen from './Components/ShareScreen';

const FullScreen = (props) =>
{
	const { handleMaxScreen, duration, handleEndCall, callType, callerData, handleScreenShare, updateCamera, localStream, remoteStream, screenStreamRef, reconectionRef } = props;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const callData = useSelector((state:any) => state?.call);
	const callDetails = useSelector((state:any) => state?.mypresence?.callDetails);
	const userData = useSelector((state:any) => state?.mysession?.session);
	const { handleAudioOff } = useContext(CallContext);
	const localVideoStreemRef = useRef<any>(null);
	const remoteVideoStreemRef = useRef<any>(null);

	useEffect(() =>
	{
		if (localVideoStreemRef.current)
		{
			localVideoStreemRef.current.srcObject = localStream;
		}

		if (remoteVideoStreemRef.current)
		{
			remoteVideoStreemRef.current.srcObject = remoteStream;
		}
	}, [ localStream, remoteStream, callData?.videoCall, callData?.roomModeView ]);

	const selfData:any = callDetails && Object.values(callDetails?.participants)?.find((i:any) => i?.id === userData?.userId);

	const getImageUrl = useCallback((participantData:any) =>
	{
		// If participant has an ID, try S3 URL pattern
		if (participantData?.id)
		{
			return `${process.env.REACT_APP_S3_BASE_URL}/profile/${participantData?.id}_profile.jpeg?t=${Date.now().valueOf()}`;
		}

		// Fallback to profileImg if available
		if (participantData?.profileImg)
		{
			return participantData?.profileImg;
		}

		// Fallback to default image
		return '/images/icons/defaultUser.svg';
	}, []);

	const callerImageRef = useRef<string>(getImageUrl(callerData));
	const selfImageRef = useRef<string>(getImageUrl(selfData));

	const handleErrorImage = (type) =>
	{
		if (type === 'caller')
		{
			callerImageRef.current = '/images/icons/defaultUser.svg';
		}
		else if (type === 'self')
		{
			selfImageRef.current = '/images/icons/defaultUser.svg';
		}
	};

	return (
		<Box sx={{
			position      : 'absolute',
			height        : '100vh',
			display       : 'flex',
			flexDirection : 'column',
			bgcolor       : '#002a3a',
			top           : 0,
			right         : 0,
			transform     : 'translate(0px, 0px) !important',
			width         : '100vw',
			zIndex        : 1111,
			overflowY     : 'auto'
		}}
		>
			{/* App Bar */}
			<Box sx={{ backgroundColor: '#173F4D' }}>
				<Toolbar>
					<Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
						<TbWindowMinimize style={{ color: 'white', marginRight: '8px' }} onClick={handleMaxScreen}/>
						<Typography variant='h6' component='div' sx={{ color: 'white', cursor: 'pointer' }} >
							{callType === 'audio' ? 'Audio Call' : 'Video Call'}
						</Typography>
					</Box>
					<Typography variant='body2' sx={{ color: 'white', mr: 2 }}>
						{callTimerFormat(duration)}
					</Typography>
				</Toolbar>
			</Box>

			{/* Video Content */}
			<Box sx={{ flexGrow: 1, p: 2, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				{callData?.roomModeView !== 'screenShare'
					? (<Grid container spacing={2} sx={{ height: '70%' }}>
						{/* Video feeds */}
						<Grid item xs={12} md={6} sx={{ height: isMobile ? '50%' : '100%' }}>
							<Box
								sx={{
									height         : '100%',
									bgcolor        : 'rgba(255, 255, 255, 0.05)',
									position       : 'relative',
									overflow       : 'hidden',
									borderRadius   : 2,
									display        : 'flex',
									alignItems     : 'center',
									justifyContent : 'center'

								}}
							>
								{/* Video placeholder */}

								{callType === 'video' &&
								<video
									id='localVideoStreem'
									ref={localVideoStreemRef}
									autoPlay
									muted
									playsInline
									style={{
										width        : '100%',
										height       : '100%',
										objectFit    : 'cover',
										borderRadius : '8px',
										display      : !callData?.webcamInProgress ? 'none' : '',
										transform    : 'scaleX(-1)'
									}}
								/>}
								{(callType === 'audio' || !callData?.webcamInProgress) &&
								<Box
									component='img'
									src={selfImageRef.current || '/images/icons/defaultUser.svg'}
									alt='Your video'
									sx={{
										width        : '200px',
										height       : '200px',
										objectFit    : 'cover',
										borderRadius : '100px'
									}}
									draggable={false}
									onError={() => handleErrorImage('self')}
								/>}

								{/* User label */}
								<Box
									sx={{
										position     : 'absolute',
										bottom       : 50,
										left         : 10,
										bgcolor      : 'rgba(0,0,0,0.5)',
										px           : 1,
										borderRadius : 1
									}}
								>
									<Typography variant='body2' sx={{ color: 'white' }}>
										You
									</Typography>
								</Box>
							</Box>
						</Grid>

						<Grid item xs={12} md={6} sx={{ height: isMobile ? '50%' : '100%' }}>
							<Box
								sx={{
									height         : '100%',
									bgcolor        : 'rgba(255, 255, 255, 0.05)',
									position       : 'relative',
									overflow       : 'hidden',
									borderRadius   : 2,
									display        : 'flex',
									alignItems     : 'center',
									justifyContent : 'center'
								}}
							>
								{/* Video placeholder */}

								{callType === 'video' &&
								<video
									ref={remoteVideoStreemRef}
									id='remoteVideo'
									autoPlay
									muted
									playsInline
									style={{
										width        : '100%',
										height       : '100%',
										objectFit    : 'cover',
										borderRadius : '8px',
										display      : callerData?.isVideoPaused ? 'none' : ''
									}}
								/>}
								{(callType === 'audio' || callerData?.isVideoPaused) &&
								<Box
									component='img'
									src={callerImageRef.current || '/images/icons/defaultUser.svg'}
									alt='Caller video'
									sx={{
										width        : '200px',
										height       : '200px',
										borderRadius : '100px',
										objectFit    : 'cover'
									}}
									draggable={false}
									onError={() => handleErrorImage('caller')}
								/>}

								{/* Remote user label */}
								<Box
									sx={{
										position     : 'absolute',
										bottom       : 50,
										left         : 10,
										bgcolor      : 'rgba(0,0,0,0.5)',
										px           : 1,
										borderRadius : 1
									}}
								>
									<Typography variant='body2' sx={{ color: 'white' }}>
										{callerData?.name ?? ''}
									</Typography>
								</Box>
							</Box>
						</Grid>
					</Grid>)
					: (
						<ShareScreen
							callType={callType}
							localStream={localStream}
							remoteStream={remoteStream}
							callData={callData}
							callDetails={callDetails}
							userData={userData}
							screenStreamRef={screenStreamRef}
						/>
					)}
			</Box>

			{/* Control Bar */}
			<Box
				sx={{
					p              : 2,
					display        : 'flex',
					justifyContent : 'center',
					bgcolor        : '#002835'
				}}
			>
				<Stack
					direction='row'
					spacing={3}
					sx={{
						p            : 1,
						// bgcolor      : 'rgba(0,0,0,0.4)',
						borderRadius : 4,
						alignItems   : 'center'
					}}
				>
					<IconButton
						onTouchStart={() => handleAudioOff(!callData?.audioInProgress)}
						onClick={() => handleAudioOff(!callData?.audioInProgress)}
						sx={{
							bgcolor   : callData?.audioInProgress ? 'rgba(255,255,255,0.2)' : 'rgba(255,0,0,0.5)',
							'&:hover' : { bgcolor: callData?.audioInProgress ? 'rgba(255,255,255,0.3)' : 'rgba(255,0,0,0.7)' },
							color     : 'white'
						}}
					>
						{callData?.audioInProgress ? <MicIcon /> : <MicOffIcon />}
					</IconButton>

					{callType === 'video' &&
					<IconButton
						onTouchStart={() => updateCamera('video', !callData?.webcamInProgress)}
						onClick={() => updateCamera('video', !callData?.webcamInProgress)}
						disabled={userData?.userId === callDetails?.screenShareId}
						sx={{
							bgcolor   : callData?.webcamInProgress ? 'rgba(255,255,255,0.2)' : 'rgba(255,0,0,0.5)',
							'&:hover' : { bgcolor: callData?.webcamInProgress ? 'rgba(255,255,255,0.3)' : 'rgba(255,0,0,0.7)' },
							color     : 'white'
						}}
					>
						{callData?.webcamInProgress ? <VideocamIcon /> : <VideocamOffIcon />}
					</IconButton>}

					{callType === 'video' &&
					<IconButton
						onTouchStart={handleScreenShare}
						onClick={handleScreenShare}
						sx={{
							bgcolor   : (callData?.roomModeView === 'screenShare' && callDetails?.screenShareId === userData?.userId) ? 'rgba(0,128,0,0.5)' : 'rgba(255,255,255,0.2)',
							'&:hover' : { bgcolor: (callData?.roomModeView === 'screenShare' && callDetails?.screenShareId === userData?.userId) ? 'rgba(0,128,0,0.7)' : 'rgba(255,255,255,0.3)' },
							color     : 'white'
						}}
					>
						{(callData?.roomModeView === 'screenShare' && callDetails?.screenShareId === userData?.userId) ? <StopScreenShareIcon /> : <ScreenShareIcon />}
					</IconButton>}

					<IconButton
						onTouchStart={handleEndCall}
						onClick={handleEndCall}
						sx={{
							bgcolor   : 'rgba(255,0,0,0.8)',
							'&:hover' : { bgcolor: 'rgba(255,0,0,1)' },
							color     : 'white'
						}}
					>
						<CallEndIcon />
					</IconButton>
				</Stack>
			</Box>
		</Box>
	);
};

export default FullScreen;